/**
 * Beds24StatusPage - Phase 5.4B
 * Admin Status Page for Beds24 Automatic Synchronization
 * 
 * Features:
 * - API connection status
 * - Auto sync ON/OFF toggle
 * - Last/Next sync info
 * - Today's statistics
 * - Sync Health Monitoring with alerts
 * - Sync logs
 * - Bootstrap controls
 * - Manual sync (emergency)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import AdminLayout from '../AdminLayout';
import { API } from '../../../lib/api';
import { toast } from 'sonner';
import {
  RefreshCw, CheckCircle, XCircle, Clock, Play, Square,
  AlertTriangle, Database, Calendar, Users, Activity,
  Settings, Download, Upload, Zap, History, ArrowRight,
  Shield, Wifi, WifiOff, AlertCircle, Heart, HeartPulse
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../../../components/ui/alert';

// Health Status Banner Component
const HealthStatusBanner = ({ health, loading }) => {
  if (loading || !health) {
    return null;
  }

  const { status, alerts } = health;
  
  if (status === 'healthy') {
    return (
      <Alert className="border-green-200 bg-green-50" data-testid="health-banner-healthy">
        <Heart className="h-4 w-4 text-green-600" />
        <AlertTitle className="text-green-800">Sistema Sano</AlertTitle>
        <AlertDescription className="text-green-700">
          La sincronizzazione Beds24 funziona correttamente. 
          {health.successful_syncs_10min > 0 && ` ${health.successful_syncs_10min} sync riusciti negli ultimi 10 minuti.`}
        </AlertDescription>
      </Alert>
    );
  }

  if (status === 'degraded') {
    return (
      <Alert className="border-yellow-200 bg-yellow-50" data-testid="health-banner-warning">
        <AlertTriangle className="h-4 w-4 text-yellow-600" />
        <AlertTitle className="text-yellow-800">Attenzione</AlertTitle>
        <AlertDescription className="text-yellow-700">
          {alerts?.map((a, i) => (
            <span key={i} className="block">{a.message}</span>
          ))}
        </AlertDescription>
      </Alert>
    );
  }

  if (status === 'critical') {
    return (
      <Alert className="border-red-200 bg-red-50" data-testid="health-banner-critical">
        <AlertCircle className="h-4 w-4 text-red-600" />
        <AlertTitle className="text-red-800">Errore Critico</AlertTitle>
        <AlertDescription className="text-red-700">
          {alerts?.map((a, i) => (
            <span key={i} className="block">{a.message}</span>
          ))}
        </AlertDescription>
      </Alert>
    );
  }

  return null;
};

// Compact Status Indicator
const StatusIndicator = ({ status, label, subtext }) => {
  const statusConfig = {
    online: { color: 'bg-green-500', icon: CheckCircle },
    offline: { color: 'bg-red-500', icon: XCircle },
    warning: { color: 'bg-yellow-500', icon: AlertTriangle },
    running: { color: 'bg-blue-500 animate-pulse', icon: RefreshCw },
    disabled: { color: 'bg-gray-400', icon: Square },
  };

  const config = statusConfig[status] || statusConfig.disabled;
  const Icon = config.icon;

  return (
    <div className="flex items-center gap-3">
      <div className={`w-3 h-3 rounded-full ${config.color}`} />
      <div className="flex-1">
        <p className="font-medium text-sm">{label}</p>
        {subtext && <p className="text-xs text-gray-500">{subtext}</p>}
      </div>
    </div>
  );
};

// Metric card
const MetricCard = ({ title, value, icon: Icon, color = 'gray', subValue }) => {
  const colorClasses = {
    green: 'bg-green-50 border-green-200 text-green-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
  };

  return (
    <div className={`rounded-lg border p-3 ${colorClasses[color]}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" />
        <span className="text-xs font-medium">{title}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
      {subValue && <p className="text-xs opacity-75 mt-1">{subValue}</p>}
    </div>
  );
};

// Health Indicator Mini Card
const HealthIndicatorCard = ({ health }) => {
  if (!health) return null;

  const statusConfig = {
    healthy: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', icon: Heart, label: 'Sano' },
    degraded: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', icon: AlertTriangle, label: 'Ritardo' },
    critical: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: AlertCircle, label: 'Errore' },
  };

  const config = statusConfig[health.status] || statusConfig.healthy;
  const Icon = config.icon;

  return (
    <Card className={`${config.border} ${config.bg}`}>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon className={`w-5 h-5 ${config.text}`} />
            <div>
              <p className={`font-medium text-sm ${config.text}`}>Salute Sync</p>
              <p className={`text-xs ${config.text} opacity-75`}>{config.label}</p>
            </div>
          </div>
          <Badge variant={health.status === 'healthy' ? 'default' : health.status === 'degraded' ? 'secondary' : 'destructive'}>
            {health.status !== 'healthy' 
              ? (health.consecutive_failures > 0 ? `${health.consecutive_failures} fallimenti` : 'Attenzione')
              : 'OK'}
          </Badge>
        </div>
        {health.alerts?.length > 0 && (
          <div className="mt-2 pt-2 border-t border-current/10">
            {health.alerts.slice(0, 2).map((alert, i) => (
              <p key={i} className={`text-xs ${config.text}`}>{alert.message}</p>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function Beds24StatusPage() {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [health, setHealth] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [bootstrapDialog, setBootstrapDialog] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const lastToastRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (!token) return;

    try {
      const [statusRes, settingsRes, logsRes, healthRes] = await Promise.all([
        fetch(`${API}/admin/beds24/auto/status`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API}/admin/beds24/auto/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API}/admin/beds24/auto/logs?limit=20`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API}/admin/beds24/health`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (statusRes.ok) {
        setStatus(await statusRes.json());
      }
      if (settingsRes.ok) {
        setSettings(await settingsRes.json());
      }
      if (logsRes.ok) {
        const logsData = await logsRes.json();
        setLogs(logsData.logs || []);
      }
      if (healthRes.ok) {
        const healthData = await healthRes.json();
        setHealth(healthData);
        
        // Show toast only once per critical state change
        if (healthData.status === 'critical' && lastToastRef.current !== 'critical') {
          toast.error('Sincronizzazione Beds24 in errore critico');
          lastToastRef.current = 'critical';
        } else if (healthData.status === 'healthy' && lastToastRef.current === 'critical') {
          toast.success('Sincronizzazione Beds24 ripristinata');
          lastToastRef.current = 'healthy';
        }
      }
    } catch (e) {
      console.error('Error fetching Beds24 status:', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    
    fetchData();
    // Auto refresh every 10 seconds
    const interval = setInterval(() => {
      if (token) fetchData();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchData, token]);

  const toggleAutoSync = async () => {
    if (!token) return;

    try {
      const endpoint = status?.auto_sync_enabled 
        ? `${API}/admin/beds24/auto/stop`
        : `${API}/admin/beds24/auto/start`;

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success(status?.auto_sync_enabled ? 'Auto sync disattivato' : 'Auto sync attivato');
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Errore');
      }
    } catch (e) {
      toast.error('Errore nella modifica');
    }
  };

  const runManualSync = async (forceFull = false) => {
    if (!token) return;
    setSyncing(true);

    try {
      const res = await fetch(`${API}/admin/beds24/auto/sync?force_full=${forceFull}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json();
      if (res.ok) {
        toast.success(`Sync completato: ${data.created || 0} create, ${data.updated || 0} aggiornate`);
        fetchData();
      } else {
        toast.error(data.detail || 'Errore sync');
      }
    } catch (e) {
      toast.error('Errore sync');
    } finally {
      setSyncing(false);
    }
  };

  const runBootstrap = async () => {
    if (!token) return;
    setBootstrapping(true);

    try {
      const res = await fetch(`${API}/admin/beds24/auto/bootstrap`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json();
      if (res.ok) {
        toast.success(`Bootstrap completato: ${data.created || 0} create, ${data.updated || 0} aggiornate`);
        setBootstrapDialog(false);
        fetchData();
      } else {
        toast.error(data.detail || 'Errore bootstrap');
      }
    } catch (e) {
      toast.error('Errore bootstrap');
    } finally {
      setBootstrapping(false);
    }
  };

  const ensureIndexes = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/admin/beds24/ensure-indexes`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('Indici idempotenza creati con successo');
      } else {
        toast.error(data.message || 'Errore creazione indici');
      }
    } catch (e) {
      toast.error('Errore creazione indici');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('it-IT');
    } catch {
      return dateStr;
    }
  };

  const formatDuration = (minutes) => {
    if (minutes === null || minutes === undefined) return '-';
    if (minutes < 1) return 'meno di 1 min';
    if (minutes < 60) return `${minutes} min fa`;
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m fa`;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  if (!status?.available) {
    return (
      <AdminLayout>
        <div className="max-w-2xl mx-auto mt-12 text-center">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Beds24 non configurato</h1>
          <p className="text-gray-600 mb-4">
            Per abilitare la sincronizzazione automatica, configura le credenziali Beds24 nel file .env:
          </p>
          <div className="bg-gray-100 rounded-lg p-4 text-left font-mono text-sm">
            <p>BEDS24_PROPERTY_ID=your_property_id</p>
            <p>BEDS24_REFRESH_TOKEN=your_refresh_token</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-6xl mx-auto" data-testid="beds24-status-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Beds24 Status</h1>
            <p className="text-sm text-gray-500">Sincronizzazione automatica e monitoraggio salute</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Aggiorna
            </Button>
          </div>
        </div>

        {/* Health Status Banner */}
        <HealthStatusBanner health={health} loading={loading} />

        {/* Status Cards Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* API Status */}
          <Card>
            <CardContent className="pt-4">
              <StatusIndicator
                status={status?.api_online ? 'online' : 'offline'}
                label={status?.api_online ? 'API Online' : 'API Offline'}
                subtext={status?.api_online ? 'Connesso a Beds24' : 'Verifica credenziali'}
              />
            </CardContent>
          </Card>

          {/* Auto Sync Status */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <StatusIndicator
                  status={status?.auto_sync_enabled ? (status?.is_running ? 'running' : 'online') : 'disabled'}
                  label={status?.auto_sync_enabled ? 'Auto Sync ON' : 'Auto Sync OFF'}
                  subtext={status?.is_running ? 'In corso...' : `Ogni ${settings?.sync_interval_seconds || 60}s`}
                />
                <Switch
                  checked={status?.auto_sync_enabled || false}
                  onCheckedChange={toggleAutoSync}
                  data-testid="auto-sync-toggle"
                />
              </div>
            </CardContent>
          </Card>

          {/* Scheduler Running */}
          <Card>
            <CardContent className="pt-4">
              <StatusIndicator
                status={status?.scheduler_running ? 'running' : (status?.auto_sync_enabled ? 'warning' : 'disabled')}
                label={status?.scheduler_running ? 'Scheduler Attivo' : 'Scheduler Fermo'}
                subtext={status?.scheduler_running ? 'Esecuzione automatica' : (status?.auto_sync_enabled ? 'Attenzione!' : 'Auto sync disattivato')}
              />
            </CardContent>
          </Card>

          {/* Bootstrap Status */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <StatusIndicator
                  status={settings?.bootstrap_completed ? 'online' : 'warning'}
                  label={settings?.bootstrap_completed ? 'Bootstrap OK' : 'Bootstrap Richiesto'}
                  subtext={settings?.bootstrap_completed 
                    ? `Dal ${formatDate(settings?.bootstrap_date)?.split(',')[0]}` 
                    : 'Esegui prima di attivare'}
                />
                {!settings?.bootstrap_completed && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setBootstrapDialog(true)}
                  >
                    <Database className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Health Indicator + Sync Info Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Health Indicator */}
          <HealthIndicatorCard health={health} />

          {/* Last Sync */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <History className="w-4 h-4" />
                Ultimo Sync
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-semibold">{formatDate(status?.last_sync)}</p>
              {status?.last_sync_result && (
                <div className="mt-2 text-sm text-gray-600">
                  <p>Durata: {status.last_sync_result.duration_ms || 0}ms</p>
                  <p className="text-xs mt-1">
                    <span className="text-green-600">+{status.last_sync_result.created || 0}</span>
                    {' '}<span className="text-blue-600">↻{status.last_sync_result.updated || 0}</span>
                    {' '}<span className="text-yellow-600">✗{status.last_sync_result.cancelled || 0}</span>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Next Sync */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Prossimo Sync
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-semibold">
                {status?.auto_sync_enabled ? formatDate(status?.next_sync) : 'Auto sync disattivato'}
              </p>
              {status?.current_cursor && (
                <p className="mt-2 text-xs text-gray-500 font-mono truncate">
                  Cursor: {status.current_cursor.substring(0, 20)}...
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Today's Stats */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Statistiche Oggi</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <MetricCard 
                title="Importati" 
                value={status?.imported_today || 0} 
                icon={Download}
                color="green"
              />
              <MetricCard 
                title="Aggiornati" 
                value={status?.updated_today || 0} 
                icon={RefreshCw}
                color="blue"
              />
              <MetricCard 
                title="Cancellati" 
                value={status?.cancelled_today || 0} 
                icon={XCircle}
                color="yellow"
              />
              <MetricCard 
                title="Errori" 
                value={status?.errors_today || 0} 
                icon={AlertTriangle}
                color={status?.errors_today > 0 ? 'red' : 'gray'}
              />
              <MetricCard 
                title="Tempo Medio" 
                value={`${status?.avg_sync_time_ms || 0}ms`} 
                icon={Zap}
                color="gray"
              />
              <MetricCard 
                title="Eventi" 
                value={status?.last_sync_result?.events_generated || 0} 
                icon={Activity}
                color={status?.last_sync_result?.events_generated > 0 ? 'blue' : 'gray'}
                subValue="Event Bridge"
              />
            </div>
          </CardContent>
        </Card>

        {/* Last Error */}
        {status?.last_error && (
          <Card className="border-red-200 bg-red-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-red-800 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Ultimo Errore
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-red-700">{status.last_error}</p>
            </CardContent>
          </Card>
        )}

        {/* Manual Sync & Admin Tools */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Manual Sync */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Sync Manuale (Emergenza)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Usa il sync manuale solo in caso di emergenza.
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => runManualSync(false)}
                  disabled={syncing || status?.is_running}
                >
                  {syncing ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Incrementale
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => runManualSync(true)}
                  disabled={syncing || status?.is_running}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Completo
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Admin Tools */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Strumenti Admin
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Inizializza indici per idempotenza.
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={ensureIndexes}
                >
                  <Database className="w-4 h-4 mr-2" />
                  Crea Indici
                </Button>
                {!settings?.bootstrap_completed && (
                  <Button 
                    size="sm"
                    onClick={() => setBootstrapDialog(true)}
                  >
                    <Database className="w-4 h-4 mr-2" />
                    Bootstrap
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Logs */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Log Recenti
            </CardTitle>
          </CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">Nessun log disponibile</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {logs.map((log, i) => (
                  <div 
                    key={log.id || i}
                    className={`p-2 rounded-lg border text-sm ${
                      log.status === 'completed' ? 'bg-green-50 border-green-200' :
                      log.status === 'failed' ? 'bg-red-50 border-red-200' :
                      'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium flex items-center gap-2">
                        {log.status === 'completed' && <CheckCircle className="w-3 h-3 text-green-600" />}
                        {log.status === 'failed' && <XCircle className="w-3 h-3 text-red-600" />}
                        {log.type === 'bootstrap' ? 'Bootstrap' : 
                         log.type === 'manual' ? 'Manuale' : 'Automatico'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatDate(log.start_time)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-600">
                      <span>{log.duration_ms || 0}ms</span>
                      <span>Ricevuti: {log.total_received || 0}</span>
                      <span className="text-green-600">+{log.created || 0}</span>
                      <span className="text-blue-600">↻{log.updated || 0}</span>
                      {log.cancelled > 0 && (
                        <span className="text-yellow-600">✗{log.cancelled}</span>
                      )}
                      {log.events_generated > 0 && (
                        <span className="text-purple-600">⚡{log.events_generated}</span>
                      )}
                      {log.errors?.length > 0 && (
                        <span className="text-red-600">❌{log.errors.length}</span>
                      )}
                    </div>
                    {log.status === 'failed' && log.error && (
                      <p className="mt-1 text-xs text-red-600 truncate">{log.error}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bootstrap Dialog */}
        <Dialog open={bootstrapDialog} onOpenChange={setBootstrapDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Bootstrap Iniziale</DialogTitle>
              <DialogDescription>
                Il bootstrap importa tutte le prenotazioni storiche da Beds24 
                <strong> SENZA generare automazioni </strong> 
                (niente conferme, reminder, review request per prenotazioni esistenti).
                <br /><br />
                Solo le <strong>nuove prenotazioni</strong> dopo il bootstrap 
                genereranno automazioni automatiche.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBootstrapDialog(false)}>
                Annulla
              </Button>
              <Button onClick={runBootstrap} disabled={bootstrapping}>
                {bootstrapping ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Database className="w-4 h-4 mr-2" />
                )}
                Esegui Bootstrap
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
